import React, { useState, useEffect, useRef, memo } from 'react';
import toast from 'react-hot-toast';
import { transactionService } from '../../services/transactionService';
import { platnosciService, komunikatBledu } from '../../services/platnosciService';
import ReturnModal from './ReturnModal';
import ZmienPlatnoscModal from '../kasabank/ZmienPlatnoscModal';

// Funkcje pomocnicze do dat
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

const getYesterdayDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const getMonthRange = () => {
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  return {
    from: firstDay.toISOString().split('T')[0],
    to: lastDay.toISOString().split('T')[0]
  };
};

const TransactionsList = ({ onTransactionSelect, onCorrectionClick, isAdmin = false, locationId = null, onFiscalPrint = null, canPrintCopy = true, canReturn = true, refreshTrigger = 0 }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnTransaction, setReturnTransaction] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  // Zmiana platnosci (tylko w otwartej zmianie): dane z GET /platnosc/mozliwosci po kliknieciu;
  // platnoscBlokady[id] = powod_blokady -> przycisk szary z tooltipem
  const [platnoscTransakcja, setPlatnoscTransakcja] = useState(null);
  const [platnoscSprawdzanieId, setPlatnoscSprawdzanieId] = useState(null);
  const [platnoscBlokady, setPlatnoscBlokady] = useState({});
  // Blokada wielokrotnej fiskalizacji tego samego paragonu — ref (natychmiastowy
  // guard, zanim re-render) + stan (do wyszarzenia przycisku). Bez tego szybkie
  // klikniecia 'Ponow fiskalizacje' fiskalizowaly paragon kilka razy (duplikaty).
  const fiscalizingRef = useRef(new Set());
  const [fiscalizingIds, setFiscalizingIds] = useState(() => new Set());
  const [dateFilter, setDateFilter] = useState('today');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const [filters, setFilters] = useState({
    limit: 50,
    status: 'completed',
    date_from: getTodayDate(),
    date_to: getTodayDate(),
    location_id: locationId,
    only_corrections: false
  });

  useEffect(() => {
    loadTransactions();
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // [REFRESH-paragony] Odswiez liste po zapisaniu nowej transakcji (bump refreshTrigger z POS)
  useEffect(() => {
    loadTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // [REFRESH-paragony] CYKLICZNE bezpieczenstwo - gdy aktywny widok paragonow
  // (ten komponent montowany jest tylko na zakladce Paragony) odswiezaj co 15s.
  useEffect(() => {
    const interval = setInterval(() => {
      loadTransactions();
    }, 15000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Zamknij menu akcji po kliknięciu gdziekolwiek
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Aktualizuj location_id w filtrach gdy props się zmieni
  useEffect(() => {
    if (locationId !== filters.location_id) {
      setFilters(prev => ({
        ...prev,
        location_id: locationId
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const loadTransactions = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await transactionService.getTransactions(filters);
      if (response.success) {
        setTransactions(response.data.transactions || []);
      } else {
        setError(response.error || 'Błąd pobierania transakcji');
      }
    } catch (err) {
      setError('Błąd połączenia: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // "Platnosc" - sprawdz mozliwosci na backendzie, potem otworz modal (albo pokaz powod blokady)
  const handlePlatnoscClick = async (transaction) => {
    setPlatnoscSprawdzanieId(transaction.id);
    try {
      const m = await platnosciService.mozliwosci(transaction.id);
      if (!m.mozna_zmienic) {
        const powod = m.powod_blokady || 'Zmiana płatności niedostępna';
        setPlatnoscBlokady(prev => ({ ...prev, [transaction.id]: powod }));
        toast.error(powod);
        return;
      }
      setPlatnoscBlokady(prev => {
        if (!prev[transaction.id]) return prev;
        const next = { ...prev };
        delete next[transaction.id];
        return next;
      });
      setPlatnoscTransakcja({
        id: transaction.id,
        numer_paragonu: transaction.receipt_number || transaction.numer_paragonu || null,
        forma_platnosci: m.obecna || transaction.payment_method,
        kwota: transaction.total_amount,
        fiskalizacja: m.fiskalizacja,
        formy: m.formy
      });
    } catch (err) {
      toast.error(komunikatBledu(err, 'Nie udało się sprawdzić możliwości zmiany płatności'));
    } finally {
      setPlatnoscSprawdzanieId(null);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Jeśli zmieniono datę ręcznie, ustaw tryb custom
    if (name === 'date_from' || name === 'date_to') {
      setDateFilter('custom');
    }
  };

  const handleDateFilterChange = (mode) => {
    setDateFilter(mode);
    if (mode === 'today') {
      const today = getTodayDate();
      setFilters(prev => ({
        ...prev,
        date_from: today,
        date_to: today
      }));
    } else if (mode === 'yesterday') {
      const yesterday = getYesterdayDate();
      setFilters(prev => ({
        ...prev,
        date_from: yesterday,
        date_to: yesterday
      }));
    } else if (mode === 'month') {
      const range = getMonthRange();
      setFilters(prev => ({
        ...prev,
        date_from: range.from,
        date_to: range.to
      }));
    }
    // custom - nie zmieniamy dat, użytkownik wybierze sam
  };

  const handleDeleteTransaction = async (transaction) => {
    if (!window.confirm(`Czy na pewno chcesz usunąć ${transaction.receipt_number || `transakcję #${transaction.id}`}?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await transactionService.deleteTransaction(transaction.id);
      if (response.success) {
        setSuccessMessage(`Transakcja ${response.data.receipt_number} została usunięta`);
        setTimeout(() => setSuccessMessage(''), 3000);
        loadTransactions(); // Odśwież listę
      } else {
        setError(response.error || 'Błąd usuwania transakcji');
      }
    } catch (err) {
      setError('Błąd połączenia: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReturnClick = (transaction) => {
    setReturnTransaction(transaction);
    setShowReturnModal(true);
  };

  const handleReturnComplete = (returnData) => {
    setSuccessMessage(`Zwrot ${returnData.return_number} utworzony: ${returnData.total_brutto} zł`);
    setTimeout(() => setSuccessMessage(''), 5000);
    loadTransactions(); // Odśwież listę
  };

  const handleStatusChange = async (transaction, newStatus) => {
    setLoading(true);
    try {
      const response = await transactionService.updateTransactionStatus(transaction.id, newStatus);
      if (response.success) {
        setSuccessMessage(`Status transakcji zmieniony na: ${newStatus}`);
        setTimeout(() => setSuccessMessage(''), 3000);
        loadTransactions(); // Odśwież listę
      } else {
        setError(response.error || 'Błąd zmiany statusu');
      }
    } catch (err) {
      setError('Błąd połączenia: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (transaction) => {
    // Jeśli to zwrot (korekta paragonu), wyświetl "KPAR"
    if (transaction.typ_transakcji === 'zwrot' || transaction.document_type === 'zwrot') {
      return {
        text: 'KPAR',
        backgroundColor: 'var(--danger-light)',
        color: 'var(--danger)'
      };
    }

    // Jeśli typ transakcji to korekta faktury, wyświetl "KOREKTA"
    if (transaction.transaction_type === 'korekta') {
      return {
        text: 'KOREKTA',
        backgroundColor: 'var(--danger-light)',
        color: 'var(--danger)'
      };
    }

    // Standardowe statusy
    switch (transaction.status) {
      case 'zakonczony':
        return {
          text: 'OK',
          backgroundColor: 'var(--success-light)',
          color: 'var(--success)'
        };
      case 'zatwierdzony':
        return {
          text: 'OK',
          backgroundColor: 'var(--success-light)',
          color: 'var(--success)'
        };
      case 'draft':
        return {
          text: 'SZKIC',
          backgroundColor: 'var(--warning-light)',
          color: 'var(--warning)'
        };
      case 'w_trakcie':
        return {
          text: 'W TOKU',
          backgroundColor: 'var(--info-light)',
          color: 'var(--info)'
        };
      default:
        return {
          text: transaction.status?.toUpperCase() || 'NIEZNANY',
          backgroundColor: 'var(--bg)',
          color: 'var(--text-muted)'
        };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      return timeStr.substring(0, 5); // HH:MM
    } catch {
      return timeStr;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount || 0);
  };

  return (
    <div style={{ 
      backgroundColor: 'var(--card-bg)', 
      borderRadius: '8px', 
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <h3 style={{ margin: 0, color: 'var(--text)' }}>
          📄 Lista Paragonów
        </h3>
        <button
          onClick={loadTransactions}
          disabled={loading}
          style={{
            padding: '6px 12px',
            border: '1px solid var(--info)', color: 'var(--info)',
            fontSize: '12px'
          }}
        >
          {loading ? '⟳ Odświeżanie...' : '🔄 Odśwież'}
        </button>
      </div>

      {/* Filtry */}
      <div style={{ 
        display: 'flex', 
        gap: '12px', 
        marginBottom: '20px', 
        padding: '15px',
        backgroundColor: 'var(--bg)',
        borderRadius: '6px',
        flexWrap: 'wrap',
        alignItems: 'flex-end'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: 'var(--text-muted)', fontWeight: '500' }}>
            Status
          </label>
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              fontSize: '12px',
              height: '34px',
              backgroundColor: 'var(--card-bg)'
            }}
          >
            <option value="completed">Sfinalizowane</option>
            <option value="draft">Szkice</option>
            <option value="all">Wszystkie</option>
          </select>
        </div>

        {/* Filtr: tylko korekty */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: 'var(--text-muted)', fontWeight: '500' }}>
            Typ
          </label>
          <label style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '6px 10px',
            border: filters.only_corrections ? '2px solid var(--warning)' : '1px solid var(--border)',
            borderRadius: '6px',
            fontSize: '12px',
            height: '34px',
            backgroundColor: filters.only_corrections ? 'var(--warning-light, #fff3cd)' : 'var(--card-bg)',
            cursor: 'pointer',
            userSelect: 'none'
          }}>
            <input
              type="checkbox"
              checked={!!filters.only_corrections}
              onChange={(e) => handleFilterChange('only_corrections', e.target.checked)}
              style={{ margin: 0 }}
            />
            <span>📋 Tylko korekty</span>
          </label>
        </div>

        {/* Szybkie filtry dat */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: 'var(--text-muted)', fontWeight: '500' }}>
            Okres
          </label>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => handleDateFilterChange('today')}
              style={{
                padding: '6px 10px',
                border: dateFilter === 'today' ? '2px solid var(--info)' : '1px solid var(--border)',
                fontSize: '12px',
                height: '34px',
                backgroundColor: dateFilter === 'today' ? 'var(--info-light)' : 'var(--card-bg)',
                color: dateFilter === 'today' ? 'var(--info)' : 'var(--text-secondary)',
                fontWeight: dateFilter === 'today' ? '600' : 'normal'
              }}
            >
              Dzisiaj
            </button>
            <button
              onClick={() => handleDateFilterChange('yesterday')}
              style={{
                padding: '6px 10px',
                border: dateFilter === 'yesterday' ? '2px solid var(--info)' : '1px solid var(--border)',
                fontSize: '12px',
                height: '34px',
                backgroundColor: dateFilter === 'yesterday' ? 'var(--info-light)' : 'var(--card-bg)',
                color: dateFilter === 'yesterday' ? 'var(--info)' : 'var(--text-secondary)',
                fontWeight: dateFilter === 'yesterday' ? '600' : 'normal'
              }}
            >
              Wczoraj
            </button>
            <button
              onClick={() => handleDateFilterChange('month')}
              style={{
                padding: '6px 10px',
                border: dateFilter === 'month' ? '2px solid var(--info)' : '1px solid var(--border)',
                fontSize: '12px',
                height: '34px',
                backgroundColor: dateFilter === 'month' ? 'var(--info-light)' : 'var(--card-bg)',
                color: dateFilter === 'month' ? 'var(--info)' : 'var(--text-secondary)',
                fontWeight: dateFilter === 'month' ? '600' : 'normal'
              }}
            >
              Miesiąc
            </button>
            <button
              onClick={() => handleDateFilterChange('custom')}
              style={{
                padding: '6px 10px',
                border: dateFilter === 'custom' ? '2px solid var(--info)' : '1px solid var(--border)',
                fontSize: '12px',
                height: '34px',
                backgroundColor: dateFilter === 'custom' ? 'var(--info-light)' : 'var(--card-bg)',
                color: dateFilter === 'custom' ? 'var(--info)' : 'var(--text-secondary)',
                fontWeight: dateFilter === 'custom' ? '600' : 'normal'
              }}
            >
              Zakres
            </button>
          </div>
        </div>

        {/* Pola daty */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: 'var(--text-muted)', fontWeight: '500' }}>
            Od
          </label>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => handleFilterChange('date_from', e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              fontSize: '12px',
              height: '34px',
              backgroundColor: 'var(--card-bg)'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: 'var(--text-muted)', fontWeight: '500' }}>
            Do
          </label>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => handleFilterChange('date_to', e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              fontSize: '12px',
              height: '34px',
              backgroundColor: 'var(--card-bg)'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '11px', marginBottom: '4px', color: 'var(--text-muted)', fontWeight: '500' }}>
            Limit
          </label>
          <select
            value={filters.limit}
            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
            style={{
              padding: '6px 10px',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              fontSize: '12px',
              height: '34px',
              backgroundColor: 'var(--card-bg)'
            }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={500}>500</option>
            <option value={1000}>1000</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {/* Komunikat błędu */}
      {error && (
        <div style={{
          backgroundColor: 'var(--danger-light)',
          color: 'var(--danger)',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid var(--danger-light)'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Komunikat sukcesu */}
      {successMessage && (
        <div style={{
          backgroundColor: 'var(--success-light)',
          color: 'var(--success)',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid var(--success-light)'
        }}>
          ✅ {successMessage}
        </div>
      )}      {/* Loading */}
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: 'var(--text-secondary)' 
        }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
          <div>Ładowanie transakcji...</div>
        </div>
      )}

      {/* Lista transakcji */}
      {!loading && transactions.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: 'var(--text-secondary)',
          backgroundColor: 'var(--bg)',
          borderRadius: '6px'
        }}>
          📭 Brak transakcji spełniających kryteria
        </div>
      )}

      {!loading && transactions.length > 0 && (
        <div>
          {/* Header tabeli */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '30px 40px 160px 80px 50px 80px 85px 60px 80px 60px 30px 40px 50px auto',
            gap: '8px',
            padding: '10px 12px',
            backgroundColor: 'var(--border)',
            borderRadius: '6px 6px 0 0',
            border: '1px solid var(--border)',
            fontSize: '11px',
            fontWeight: '600',
            color: 'var(--text-secondary)'
          }}>
            <div>LP</div>
            <div>ID</div>
            <div>Nr Paragonu</div>
            <div>Data</div>
            <div>Czas</div>
            <div>Kasjer</div>
            <div>Kwota</div>
            <div>Rabat</div>
            <div>Płatność</div>
            <div>Status</div>
            <div>Fisk</div>
            <div>Poz.</div>
            <div>Zwroty</div>
            <div>Akcje</div>
          </div>

          {/* Wiersze transakcji */}
          <div style={{ border: '1px solid var(--border)', borderTop: 'none' }}>
            {transactions.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((transaction, index) => (
              <div
                key={transaction.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '30px 40px 160px 80px 50px 80px 85px 60px 80px 60px 30px 40px 50px auto',
                  gap: '8px',
                  padding: '10px 12px',
                  borderBottom: index < transactions.length - 1 ? '1px solid var(--border)' : 'none',
                  backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)',
                  fontSize: '12px',
                  alignItems: 'center'
                }}
              >
                <div style={{ fontWeight: '600', color: 'var(--text-muted)', fontSize: '10px', textAlign: 'center' }}>
                  {(currentPage - 1) * PAGE_SIZE + index + 1}
                </div>
                <div style={{ fontWeight: '600', color: 'var(--info)', fontSize: '11px' }}>
                  #{transaction.id}
                </div>
                
                <div style={{ fontFamily: 'monospace', fontSize: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {transaction.has_stock_shortage === 1 && (
                    <span
                      title="Sprzedaż z brakiem magazynowym - wymaga uzupełnienia stanu"
                      style={{
                        color: 'var(--danger)',
                        cursor: 'help',
                        fontSize: '14px'
                      }}
                    >
                      📦❗
                    </span>
                  )}
                  {transaction.wz_status === 'deferred' && (
                    <span
                      title="Odroczone WZ - dokument do wygenerowania w Magazyn → Odroczone WZ"
                      style={{
                        color: 'var(--orange)',
                        cursor: 'help',
                        fontSize: '14px',
                      }}
                    >
                      📄⏳
                    </span>
                  )}
                  {transaction.wz_status === 'generated' && transaction.wz_document_number && (
                    <span
                      title={`WZ wygenerowane: ${transaction.wz_document_number}`}
                      style={{
                        color: 'var(--success)',
                        cursor: 'help',
                        fontSize: '12px',
                      }}
                    >
                      📄✅
                    </span>
                  )}
                  {transaction.receipt_number ? (
                    transaction.receipt_number
                  ) : transaction.invoice_number ? (
                    <span style={{ color: 'var(--info)' }} title="Faktura na firmę">
                      📄 {transaction.invoice_number}
                    </span>
                  ) : '-'}
                  {transaction.nabywca_nip && (
                    <span
                      title={`Paragon z NIP nabywcy: ${transaction.nabywca_nazwa || ''} NIP ${transaction.nabywca_nip}`}
                      style={{
                        fontFamily: 'inherit',
                        fontSize: '9px',
                        color: 'var(--orange)',
                        border: '1px solid var(--orange)',
                        borderRadius: '3px',
                        padding: '0 4px',
                        whiteSpace: 'nowrap',
                        maxWidth: '220px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      NIP {transaction.nabywca_nip}{transaction.nabywca_nazwa ? ` · ${transaction.nabywca_nazwa}` : ''}
                    </span>
                  )}
                </div>
                
                <div>{formatDate(transaction.transaction_date)}</div>
                <div>{formatTime(transaction.transaction_time)}</div>
                
                <div style={{ fontSize: '12px' }}>
                  {transaction.cashier || '-'}
                </div>
                
                <div style={{
                  fontWeight: '600',
                  color: transaction.transaction_type === 'korekta' || transaction.total_amount < 0 ? 'var(--danger)' : 'var(--success)'
                }}>
                  {formatCurrency(transaction.total_amount)}
                </div>

                {/* Kolumna Rabat - % i kwota */}
                <div style={{ textAlign: 'center' }}>
                  {transaction.rabat_procent > 0 || transaction.rabat_kwota > 0 ? (
                    <div style={{
                      backgroundColor: 'var(--warning-light)',
                      borderRadius: '6px',
                      padding: '2px 4px',
                      fontSize: '9px',
                      color: 'var(--warning)',
                      fontWeight: 'bold',
                      lineHeight: '1.3'
                    }}>
                      {transaction.rabat_procent > 0 && <div>-{transaction.rabat_procent}%</div>}
                      <div style={{ fontWeight: 'normal' }}>-{formatCurrency(transaction.rabat_kwota)}</div>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--border)', fontSize: '10px' }}>-</span>
                  )}
                </div>

                <div>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    backgroundColor:
                      transaction.payment_method === 'gotowka' ? 'var(--success-light)' :
                      transaction.payment_method === 'karta' ? 'var(--info-light)' :
                      transaction.payment_method === 'blik' ? 'var(--orange-light)' : 'var(--bg)',
                    color:
                      transaction.payment_method === 'gotowka' ? 'var(--success)' :
                      transaction.payment_method === 'karta' ? 'var(--info)' :
                      transaction.payment_method === 'blik' ? 'var(--orange)' : 'var(--text-secondary)'
                  }}>
                    {transaction.payment_method || 'N/A'}
                  </span>
                </div>
                
                <div>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    backgroundColor: getStatusDisplay(transaction).backgroundColor,
                    color: getStatusDisplay(transaction).color,
                    fontWeight: 'bold'
                  }}>
                    {getStatusDisplay(transaction).text}
                  </span>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <span style={{
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: transaction.fiscal_status === 'F' ? 'var(--success)' : 
                           transaction.fiscal_status === 'F!' ? 'var(--danger)' : 'var(--text-muted)'
                  }}>
                    {transaction.fiscal_status || '-'}
                  </span>
                </div>
                
                <div style={{ textAlign: 'center', fontSize: '11px' }}>
                  {transaction.items_count || 0}
                </div>
                
                {/* Kolumna zwrotów */}
                <div style={{ textAlign: 'center' }}>
                  {transaction.returns_count > 0 ? (
                    <span
                      style={{
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        backgroundColor: 'var(--warning-light)',
                        color: 'var(--warning)',
                        fontWeight: 'bold'
                      }}
                      title={`Zwrócono: ${parseFloat(transaction.returns_total || 0).toFixed(2)} zł`}
                    >
                      🔄 {transaction.returns_count}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--border)', fontSize: '10px' }}>-</span>
                  )}
                </div>
                
                {/* Menu akcji */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === transaction.id ? null : transaction.id);
                    }}
                    style={{
                      padding: '4px 10px',
                      fontSize: '11px',
                      backgroundColor: openMenuId === transaction.id ? 'var(--border)' : 'var(--card-bg)',
                      fontWeight: 'bold',
                      letterSpacing: '2px'
                    }}
                  >
                    ...
                  </button>

                  {/* Dropdown menu */}
                  {openMenuId === transaction.id && (
                    <div style={{
                      position: 'absolute',
                      right: 0,
                      top: '100%',
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--border)',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000,
                      minWidth: '160px',
                      marginTop: '4px'
                    }}>
                      {/* Szczegóły */}
                      <button
                        onClick={() => {
                          onTransactionSelect && onTransactionSelect(transaction);
                          setOpenMenuId(null);
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '6px 12px',
                          
                          backgroundColor: 'transparent',
                          textAlign: 'left',
                          fontSize: '12px',
                          borderBottom: '1px solid var(--bg)'
                        }}
                      >
                        Szczegóły
                      </button>

                      {/* Drukuj paragon/korektę - tylko z uprawnieniem */}
                      {canPrintCopy && (
                        <button
                          onClick={() => {
                            // Dla zwrotów/korekt użyj strony korekty
                            if (transaction.typ_transakcji === 'zwrot' || transaction.document_type === 'zwrot') {
                              window.open(`/pos/correction-print/${transaction.zwrot_id || transaction.id}`, '_blank');
                            } else {
                              window.open(`/pos/receipt/${transaction.id}`, '_blank');
                            }
                            setOpenMenuId(null);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '6px 12px',
                            
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            fontSize: '12px',
                            borderBottom: '1px solid var(--bg)'
                          }}
                        >
                          {transaction.typ_transakcji === 'zwrot' || transaction.document_type === 'zwrot'
                            ? 'Drukuj korektę paragonu'
                            : 'Drukuj paragon'}
                        </button>
                      )}

                      {/* Fiskalizuj - dla niezfiskalizowanych lub ponów druk dla zfiskalizowanych.
                          Pokazujemy ZAWSZE gdy status=zakonczony i typ to NIE korekta (zwroty/korekty nie fiskalizujemy).
                          Jesli onFiscalPrint nie jest przekazany (drukarka nie polaczona przez Web Serial) -
                          przycisk pokazuje toast z info zeby polaczyc drukarke, zamiast sie ukrywac. */}
                      {transaction.status === 'zakonczony'
                        && transaction.typ_transakcji !== 'zwrot'
                        && transaction.document_type !== 'zwrot' && (
                        <button
                          disabled={!onFiscalPrint || fiscalizingIds.has(transaction.id)}
                          onClick={async () => {
                            if (!onFiscalPrint) {
                              toast.error('Drukarka fiskalna nie jest polaczona. Polacz ja przyciskiem "Drukarka" w gornym pasku.', { duration: 6000 });
                              setOpenMenuId(null);
                              return;
                            }
                            // Guard: paragon juz w trakcie fiskalizacji -> ignoruj
                            // kolejne klikniecia (koniec duplikatow z szybkiego klikania).
                            if (fiscalizingRef.current.has(transaction.id)) return;
                            // Dla już zfiskalizowanych - ostrzeżenie
                            if (transaction.fiscal_status === 'F') {
                              const confirmed = window.confirm(
                                '⚠️ Ten paragon jest już oznaczony jako zafiskalizowany!\n\n' +
                                'Ponowny druk fiskalny spowoduje duplikat na drukarce.\n\n' +
                                'Czy na pewno chcesz ponownie wydrukować paragon fiskalny?'
                              );
                              if (!confirmed) {
                                setOpenMenuId(null);
                                return;
                              }
                            }
                            fiscalizingRef.current.add(transaction.id);
                            setFiscalizingIds(new Set(fiscalizingRef.current));
                            try {
                              await onFiscalPrint(transaction);
                              loadTransactions();
                            } finally {
                              fiscalizingRef.current.delete(transaction.id);
                              setFiscalizingIds(new Set(fiscalizingRef.current));
                              setOpenMenuId(null);
                            }
                          }}
                          title={!onFiscalPrint ? 'Drukarka fiskalna nie jest polaczona' : ''}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '6px 12px',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            fontSize: '12px',
                            color: (!onFiscalPrint || fiscalizingIds.has(transaction.id)) ? 'var(--text-muted)' :
                                   transaction.fiscal_status === 'F' ? 'var(--purple)' :
                                   transaction.fiscal_status === 'F!' ? 'var(--danger)' : 'var(--info)',
                            opacity: (!onFiscalPrint || fiscalizingIds.has(transaction.id)) ? 0.7 : 1,
                            cursor: fiscalizingIds.has(transaction.id) ? 'wait' : 'pointer',
                            borderBottom: '1px solid var(--bg)'
                          }}
                        >
                          {fiscalizingIds.has(transaction.id) ? '⏳ Fiskalizuję...' :
                           !onFiscalPrint ? '🖨️ Fiskalizuj (polacz drukarke)' :
                           transaction.fiscal_status === 'F' ? '🖨️ Ponów druk fiskalny' :
                           transaction.fiscal_status === 'F!' ? '⚠️ Ponów fiskalizację' : '🖨️ Fiskalizuj'}
                        </button>
                      )}

                      {/* Zwrot - dla zakończonych transakcji sprzedaży - tylko z uprawnieniem */}
                      {canReturn && transaction.status === 'zakonczony' && (!transaction.typ_transakcji || ['sprzedaz', 'sale'].includes(transaction.transaction_type || transaction.typ_transakcji)) && transaction.typ_transakcji !== 'zwrot' && (
                        <button
                          onClick={() => {
                            handleReturnClick(transaction);
                            setOpenMenuId(null);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '6px 12px',
                            
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            fontSize: '12px',
                            color: 'var(--orange)',
                            borderBottom: '1px solid var(--bg)'
                          }}
                        >
                          Zwrot
                        </button>
                      )}

                      {/* Zmiana platnosci - tylko zakonczone sprzedaze; backend decyduje (otwarta zmiana, czysta forma) */}
                      {transaction.status === 'zakonczony'
                        && transaction.typ_transakcji !== 'zwrot'
                        && transaction.document_type !== 'zwrot' && (
                        <button
                          disabled={!!platnoscBlokady[transaction.id] || platnoscSprawdzanieId === transaction.id}
                          title={platnoscBlokady[transaction.id] || 'Zmień formę płatności (tylko w otwartej zmianie)'}
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            handlePlatnoscClick(transaction);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '6px 12px',
                            backgroundColor: 'transparent',
                            textAlign: 'left',
                            fontSize: '12px',
                            color: platnoscBlokady[transaction.id] ? 'var(--text-muted)' : 'var(--info)',
                            opacity: platnoscBlokady[transaction.id] ? 0.7 : 1,
                            cursor: platnoscBlokady[transaction.id] ? 'not-allowed'
                              : (platnoscSprawdzanieId === transaction.id ? 'wait' : 'pointer'),
                            borderBottom: '1px solid var(--bg)'
                          }}
                        >
                          {platnoscSprawdzanieId === transaction.id ? '⏳ Sprawdzam...' : '💱 Płatność'}
                        </button>
                      )}

                      {/* Akcje dla szkiców (admin) */}
                      {isAdmin && ['draft', 'w_trakcie'].includes(transaction.status) && (
                        <>
                          {transaction.status === 'w_trakcie' && (
                            <button
                              onClick={() => {
                                handleStatusChange(transaction, 'draft');
                                setOpenMenuId(null);
                              }}
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '6px 12px',
                                
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                fontSize: '12px',
                                color: 'var(--warning)',
                                borderBottom: '1px solid var(--bg)'
                              }}
                            >
                              Wstrzymaj
                            </button>
                          )}

                          {transaction.status === 'draft' && (
                            <button
                              onClick={() => {
                                handleStatusChange(transaction, 'w_trakcie');
                                setOpenMenuId(null);
                              }}
                              style={{
                                display: 'block',
                                width: '100%',
                                padding: '6px 12px',
                                
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                fontSize: '12px',
                                color: 'var(--info)',
                                borderBottom: '1px solid var(--bg)'
                              }}
                            >
                              Wznów
                            </button>
                          )}

                          <button
                            onClick={() => {
                              handleDeleteTransaction(transaction);
                              setOpenMenuId(null);
                            }}
                            style={{
                              display: 'block',
                              width: '100%',
                              padding: '6px 12px',
                              
                              backgroundColor: 'transparent',
                              textAlign: 'left',
                              fontSize: '12px',
                              color: 'var(--danger)'
                            }}
                          >
                            Usuń
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Paginacja */}
          {(() => {
            const totalPages = Math.ceil(transactions.length / PAGE_SIZE);
            if (totalPages <= 1) return null;
            return (
              <div style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px',
                padding: '8px', borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)',
                backgroundColor: 'var(--bg)'
              }}>
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage === 1 ? 'default' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}>
                  &larr;
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                  .map((p, idx, arr) => (
                    <React.Fragment key={p}>
                      {idx > 0 && arr[idx - 1] !== p - 1 && <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>...</span>}
                      <button onClick={() => setCurrentPage(p)}
                        style={{
                          padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
                          border: p === currentPage ? '2px solid var(--primary)' : '1px solid var(--border)',
                          backgroundColor: p === currentPage ? 'var(--primary)' : 'var(--card-bg)',
                          color: p === currentPage ? '#fff' : 'var(--text-secondary)',
                          fontWeight: p === currentPage ? '700' : '400'
                        }}>
                        {p}
                      </button>
                    </React.Fragment>
                  ))}
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', cursor: currentPage === totalPages ? 'default' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1 }}>
                  &rarr;
                </button>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                  Strona {currentPage}/{totalPages}
                </span>
              </div>
            );
          })()}

          {/* Footer z podsumowaniem */}
          <div style={{
            padding: '10px 15px',
            backgroundColor: 'var(--bg)',
            borderRadius: '0 0 6px 6px',
            border: '1px solid var(--border)',
            borderTop: 'none',
            fontSize: '12px',
            color: 'var(--text-secondary)'
          }}>
            Znaleziono: <strong>{transactions.length}</strong> transakcji |
            Łączna wartość: <strong>
              {formatCurrency(
                transactions.reduce((sum, t) => sum + (t.total_amount || 0), 0)
              )}
            </strong>
          </div>
        </div>
      )}
      
      {/* Modal zwrotu */}
      <ReturnModal
        isOpen={showReturnModal}
        onClose={() => {
          setShowReturnModal(false);
          setReturnTransaction(null);
        }}
        transaction={returnTransaction}
        onReturnComplete={handleReturnComplete}
      />

      {/* Modal zmiany platnosci (wspolny z Kasa/Bank) */}
      <ZmienPlatnoscModal
        isOpen={!!platnoscTransakcja}
        onClose={() => setPlatnoscTransakcja(null)}
        onSuccess={() => { setPlatnoscTransakcja(null); loadTransactions(); }}
        transakcja={platnoscTransakcja}
      />
    </div>
  );
};

export default memo(TransactionsList);
